import { Routes } from '@angular/router';
import { ShopComponent } from './shop/shop.component';
import { authGuard, checkAbandoneOrNoAddressdUser, maintenanceGuard, checkMembershipPermissions, checkNoPaymentMethodUser, maintenanceOffGuard, preventForAbandoneOrNoAddressdUser, hasSession } from './shared/guards/auth-guard.guard';
import { HomeComponent } from './home/home.component';
import { canDeactivateGuard } from './shared/guards/deactivate/can-deactivate.guard';
import { ShopOrProductComponent } from './shop/shop-or-product/shop-or-product.component';
import { productsResolver } from './resolvers/shop.resolver';
import { menuResolver } from './resolvers/menu.resolver';

export const routes: Routes = [
  {
    path: 'maintenance',
    title: 'Maintenance',
    loadComponent: () => import('./maintenance/maintenance.component').then(m => m.MaintenanceComponent),
    canActivate: [maintenanceOffGuard]
  },
  {
    path: 'ecommerce-admin/:token',
    title: 'Ecommerce Admin',
    loadComponent: () => import('./authentication/ecommerce-admin/ecommerce-admin.component').then(m => m.EcommerceAdminComponent),
    canActivate: [maintenanceGuard]
  },
  {
    path: '',
    component: HomeComponent,
    canActivate: [maintenanceGuard]
  },
  {
    path: 'login',
    title: 'Log In',
    loadComponent: () => import('./authentication/login/login.component').then(m => m.LoginComponent),
    canActivate: [maintenanceGuard, authGuard]
  },
  {
    path: 'signup',
    title: 'Sign Up',
    loadComponent: () => import('./authentication/signup/signup.component').then(m => m.SignupComponent),
    canActivate: [maintenanceGuard]
  },
  {
    path: 'pages',
    loadChildren: () => import('./pages/pages.routes').then(m => m.pagesRoutes)
  },
  {
    path: 'settings',
    loadChildren: () => import('./settings/settings.routes').then(m => m.settingsRoutes)
  },
  {
    path: 'account-recovery',
    loadChildren: () => import('./authentication/account-recovery/account-recovery.routes').then(m => m.accountRecoveryRoutes)
  },
  {
    path: 'shop',
    component: ShopComponent,
    canActivate: [maintenanceGuard, checkMembershipPermissions, checkAbandoneOrNoAddressdUser],
    canActivateChild: [maintenanceGuard],
    resolve: {
      data: productsResolver,
      menu: menuResolver
    }
  },
  {
    path: 'shop/custom-box/:bundleEditionType/:productId',
    title: 'Shop Custom Box',
    loadComponent: () => import('./shared/custom-box/custom-box.component').then(m => m.CustomBoxComponent),
    pathMatch: 'full',
    canActivate: [maintenanceGuard, checkNoPaymentMethodUser]
  },
  {
    path: 'shop/:category',
    component: ShopComponent,
    canActivate: [maintenanceGuard, checkMembershipPermissions, checkAbandoneOrNoAddressdUser],
    resolve: {
      data: productsResolver,
      menu: menuResolver
    }
  },
  {
    path: 'shop/:category/:subcategory',
    component: ShopComponent,
    canActivate: [maintenanceGuard, checkMembershipPermissions, checkAbandoneOrNoAddressdUser],
    resolve: {
      data: productsResolver,
      menu: menuResolver
    }
  },
  // This route is used for both, product detail or subcategory son navigation:
  {
    path: 'shop/:category/:subcategory/:slug',
    component: ShopOrProductComponent,
    canActivate: [maintenanceGuard, checkMembershipPermissions, checkAbandoneOrNoAddressdUser],
    resolve: {
      data: productsResolver,
      menu: menuResolver
    },
    // Re-run the resolver on every navigation to this route (including PDP→PDP jumps
    // from the search-bar dropdown). Without this Angular reuses the resolved data when
    // only the params change and ShopOrProductComponent keeps painting the previous product.
    runGuardsAndResolvers: 'always'
  },
  {
    path: 'favorites',
    title: 'Favorites',
    loadComponent: () => import('./fav-products/fav-products.component').then(m => m.FavProductsComponent),
    canActivate: [maintenanceGuard, checkMembershipPermissions]
  },
  {
    path: 'buy-again',
    title: 'Buy again',
    loadComponent: () => import('./buy-again-products/buy-again-products.component').then(m => m.BuyAgainProductsComponent),
    canActivate: [maintenanceGuard, checkMembershipPermissions]
  },
  {
    path: 'order',
    title: 'Order',
    loadComponent: () => import('./order/order.component').then(m => m.OrderComponent),
    canActivate: [maintenanceGuard, checkMembershipPermissions, preventForAbandoneOrNoAddressdUser],
    canDeactivate: [canDeactivateGuard]
  },
  {
    path: 'cart',
    title: 'Cart',
    loadComponent: () => import('./cart-preview/cart-preview.component').then(m => m.CartPreviewComponent),
    canActivate: [maintenanceGuard, checkMembershipPermissions]
  },
  {
    path: 'terms-and-conditions',
    title: 'Terms & Conditions',
    loadComponent: () => import('./authentication/signup/terms-and-conditions/terms-and-conditions.component').then(m => m.TermsAndConditionsComponent),
    canActivate: [maintenanceGuard]
  },
  {
    path: 'privacy-policy',
    title: 'Privacy Policy',
    loadComponent: () => import('./shared/privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent),
    canActivate: [maintenanceGuard]
  },
  {
    path: 'order/custom-box/:bundleEditionType',
    title: 'Order Custom Box',
    loadComponent: () => import('./shared/custom-box/custom-box.component').then(m => m.CustomBoxComponent),
    canActivate: [maintenanceGuard, checkNoPaymentMethodUser]
  },
  {
    path: 'order/custom-box/:bundleEditionType/:productId',
    title: 'Order Custom Box',
    loadComponent: () => import('./shared/custom-box/custom-box.component').then(m => m.CustomBoxComponent),
    canActivate: [maintenanceGuard, checkNoPaymentMethodUser]
  },
  {
    path: 'product/custom-box/:bundleEditionType/:productId',
    title: 'Product Custom Box',
    loadComponent: () => import('./shared/custom-box/custom-box.component').then(m => m.CustomBoxComponent),
    canActivate: [maintenanceGuard, checkNoPaymentMethodUser]
  },
  {
    path: 'signup/custom-box/:bundleEditionType/:productId',
    title: 'Sign Up Custom Box',
    loadComponent: () => import('./shared/custom-box/custom-box.component').then(m => m.CustomBoxComponent),
    canActivate: [maintenanceGuard, checkNoPaymentMethodUser]
  },
  {
    path: 'welcome',
    title: 'Welcome',
    loadComponent: () => import('./authentication/signup/welcome/welcome.component').then(m => m.WelcomeComponent),
    canActivate: [maintenanceGuard, hasSession]
  },
  {
    path: '**',
    redirectTo: ''
  }
];
